// The console's settings surface: a gear in the header opening a compact panel.
//
// The panel is a plain container of labelled rows. The clock toggle is the
// first tenant; the next preference (units, …) calls addRow rather than growing
// its own header chrome — one gear, one panel, however many rows.

export interface SettingsControl {
  root: HTMLElement;
  /** Append one labelled row: a small-caps label on the left, the caller's
   *  control on the right. */
  addRow(label: string, control: HTMLElement): void;
}

export function createSettingsControl(): SettingsControl {
  const root = document.createElement("div");
  root.className = "settings";

  const btn = document.createElement("button");
  btn.type = "button";
  btn.className = "settings-btn";
  btn.textContent = "⚙";
  btn.title = "Console settings";
  btn.setAttribute("aria-label", "Console settings");
  btn.setAttribute("aria-expanded", "false");

  const panel = document.createElement("div");
  panel.className = "settings-panel";
  panel.hidden = true;

  root.append(btn, panel);

  const setOpen = (open: boolean): void => {
    panel.hidden = !open;
    btn.setAttribute("aria-expanded", String(open));
  };

  btn.addEventListener("click", () => setOpen(panel.hidden));

  // Close on outside click / Escape. The header is rebuilt on every route
  // change, so these document listeners tear themselves down once the control
  // is no longer in the DOM — the same self-removal the resize handler uses.
  const onDocClick = (e: MouseEvent): void => {
    if (!root.isConnected) {
      document.removeEventListener("click", onDocClick);
      return;
    }
    if (!panel.hidden && e.target instanceof Node && !root.contains(e.target)) setOpen(false);
  };
  const onDocKey = (e: KeyboardEvent): void => {
    if (!root.isConnected) {
      document.removeEventListener("keydown", onDocKey);
      return;
    }
    if (e.key === "Escape" && !panel.hidden) setOpen(false);
  };
  document.addEventListener("click", onDocClick);
  document.addEventListener("keydown", onDocKey);

  return {
    root,
    addRow(label: string, control: HTMLElement): void {
      const row = document.createElement("div");
      row.className = "settings-row";
      const l = document.createElement("span");
      l.className = "settings-label";
      l.textContent = label;
      row.append(l, control);
      panel.appendChild(row);
    },
  };
}

/**
 * A small segmented switch — the settings panel's standard control, same idiom
 * as the header's Day/Dusk/Night buttons. `current` is read at build and after
 * every pick, so a control rebuilt later (the header remounts per route) always
 * opens showing the truth.
 */
export function segmented<T extends string>(
  options: readonly (readonly [T, string])[],
  current: () => T,
  onPick: (value: T) => void,
  title?: string,
): HTMLElement {
  const seg = document.createElement("div");
  seg.className = "seg";
  if (title !== undefined) seg.title = title;
  const buttons: [T, HTMLButtonElement][] = [];
  const refresh = (): void => {
    const cur = current();
    for (const [val, b] of buttons) b.classList.toggle("on", val === cur);
  };
  for (const [val, label] of options) {
    const b = document.createElement("button");
    b.type = "button";
    b.className = "seg-btn";
    b.textContent = label;
    b.addEventListener("click", () => {
      onPick(val);
      refresh();
    });
    seg.appendChild(b);
    buttons.push([val, b]);
  }
  refresh();
  return seg;
}
